import { Injectable } from "@angular/core";
import { firstValueFrom, Observable, Subject, tap } from "rxjs";

import { LoggingService } from "../logging.service";
import { Referencia } from "src/app/models/Referencia";
import { ErrorHandlerService } from "../errorHandler.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { AppConfig } from "src/app/app.config";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { OperacaoSignalR } from "../../models/OperacaoSignalR";
import { SignalrService } from "../signalr.service";
import { StorageService } from "../storage.service";
import { UsuarioPreferenciasService } from "./usuario.preferencias.service";
import { ReferenciaCabecalho } from "src/app/models/ReferenciaCabecalho";
import { EnumTipoDataControle } from "src/app/enums/EnumDataControle";
import { DataControleRepositorio } from "src/app/repositorios/dataControle.repositorio";

@Injectable()
export class UsuarioReferenciaService {
  readonly databaseName = "userdata";
  readonly collectionNameReferencia = "referencias";
  readonly collectionNameReferenciaCabecalho = "referencias-cabecalhos";

  private modificadoReferencia: Referencia;
  private modificadoReferencia$ = new Subject<Referencia>();

  constructor(
    private signalrService: SignalrService,
    private loggingService: LoggingService,
    private httpClient: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private usuarioPreferenciasService: UsuarioPreferenciasService,
    private storageService: StorageService,
    private dataControleRepositorio: DataControleRepositorio
  ) {
    signalrService
      .getMensagem()
      .subscribe((msg) => this.onMessageReceived(msg));
  }

  public async carregarReferenciasPorLei(leiId: string): Promise<Referencia[]> {
    const url = `${AppConfig.apiEndpoint}/Referencias/Referencias`;
    let requestParams = new HttpParams();
    let referencias: Referencia[] = [];
    const referenciaslocal = await this.storageService.list<Referencia>(
      this.databaseName,
      this.collectionNameReferencia,
      (r) => r.links?.find((e) => e.idLei === leiId)?.idLei === leiId
    );

    if (referenciaslocal.length > 0) {
      const cache = await this.VerificaCacheReferencias();

      if (cache) {
        return referenciaslocal;
      }
    }

    requestParams = requestParams.append("IdLei", leiId);
    requestParams = requestParams.append(
      "userId",
      this.usuarioPreferenciasService.getConfiguracoesvalue.id
    );

    await firstValueFrom(
      this.httpClient.get<Referencia[]>(url, { params: requestParams }).pipe(
        tap({
          next: (result: Referencia[]) => {
            if (result) {
              this.fromNuvem(result).catch((error) => {
                this.errorHandlerService.handleError(error);
                return new Array<Referencia>();
              });
              referencias = result;
              this.dataControleRepositorio
                .salvar(
                  this.dataControleRepositorio.generateDataControleTipo(
                    EnumTipoDataControle.DataVerificacaoCacheReferencias
                  )
                )
                .then(() => {})
                .catch((error) => this.errorHandlerService.handleError(error));
            }
          },
          error: (error) => {
            this.errorHandlerService.handleError(error);
            if (referenciaslocal) {
              referencias = referenciaslocal;
            }
          },
        })
      )
    );

    return referencias;
  }

  public async fromNuvem(referencias: Referencia[]): Promise<void> {
    await this.storageService
      .upsertMany(
        referencias,
        this.databaseName,
        this.collectionNameReferencia,
        false
      )
      .then(() => {})
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async atualizar(
    referencia: Referencia,
    sync = true
  ): Promise<Referencia> {
    if (sync) {
      const mensagem = new OperacaoSignalR();
      mensagem.dados = referencia;
      this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Referencias);
    }

    return await this.storageService
      .upsert(referencia, this.databaseName, this.collectionNameReferencia)
      .then((resultado) => {
        const novo = !resultado.dataHoraModificacao;

        this.modificadoReferencia = resultado;

        this.updateModificadoReferencia();

        if (novo) {
          this.loggingService.logEvent(
            "Leitor - atualizar - referencia",
            resultado.links.toString(),
            resultado.id
          );
        }
        return resultado;
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Referencia();
      });
  }

  public async remover(
    referencia: Referencia,
    itemId: string
  ): Promise<Referencia> {
    const refOld: Referencia = JSON.parse(JSON.stringify(referencia));
    const index = referencia.links.findIndex((item) => item.idItem === itemId);

    if (index !== -1) {
      referencia.links.splice(index, 1);
    }

    if (referencia.links.length === 0) {
      referencia.removido = true;
    }

    await this.atualizar(referencia);

    return refOld;
  }

  public async buscar(id: string): Promise<Referencia> {
    return await this.storageService
      .getByKey<Referencia>(
        id,
        this.databaseName,
        this.collectionNameReferencia
      )
      .then((referencia) => referencia)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Referencia();
      });
  }

  public async buscarLei(idLei: string): Promise<Referencia[]> {
    return await this.storageService
      .list<Referencia>(
        this.databaseName,
        this.collectionNameReferencia,
        (m) => m.links?.find((e) => e.idLei === idLei)?.idLei === idLei
      )
      .then((referencias) => referencias)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Referencia>();
      });
  }

  public async buscarReferenciasCabecalhoLocal(): Promise<
    ReferenciaCabecalho[]
    > {
    return await this.storageService
      .list<ReferenciaCabecalho>(
        this.databaseName,
        this.collectionNameReferenciaCabecalho
      )
      .then((referencias) => referencias)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<ReferenciaCabecalho>();
      });
  }

  public async buscarReferenciaLocalPorId(
    referenciaId: string
  ): Promise<Referencia> {
    return await this.storageService
      .getByKey<Referencia>(
        referenciaId,
        this.databaseName,
        this.collectionNameReferencia
      )
      .then((referencia) => referencia)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Referencia();
      });
  }

  public async carregarReferenciasCabecalho(): Promise<ReferenciaCabecalho[]> {
    const url = `${AppConfig.apiEndpoint}/Referencias/ReferenciasCabecalhos`;
    let requestParams = new HttpParams();
    let referenciasCabecalhos = new Array<ReferenciaCabecalho>();
    const referenciasLocalCabecalhos =
      await this.storageService.list<ReferenciaCabecalho>(
        this.databaseName,
        this.collectionNameReferenciaCabecalho
      );

    requestParams = requestParams.append(
      "userId",
      this.usuarioPreferenciasService.getConfiguracoesvalue.id
    );

    await firstValueFrom(
      this.httpClient
        .get<ReferenciaCabecalho[]>(url, { params: requestParams })
        .pipe(
          tap({
            next: (result: ReferenciaCabecalho[]) => {
              if (result) {
                this.storageService.upsertMany<ReferenciaCabecalho>(
                  result,
                  this.databaseName,
                  this.collectionNameReferenciaCabecalho
                );
                referenciasCabecalhos = result;
              }
            },
            error: (error) => {
              this.errorHandlerService.handleError(error);
              referenciasCabecalhos = referenciasLocalCabecalhos;
            },
          })
        )
    );
    return referenciasCabecalhos;
  }

  public async carregarReferenciaPorId(
    referenciaId: string
  ): Promise<Referencia> {
    const url = `${AppConfig.apiEndpoint}/Referencias/ReferenciaById`;
    let requestParams = new HttpParams();
    let referencia: Referencia = new Referencia();
    const referencialocal = await this.storageService.getByKey<Referencia>(
      referenciaId,
      this.databaseName,
      this.collectionNameReferencia
    );

    requestParams = requestParams.append(
      "userId",
      this.usuarioPreferenciasService.getConfiguracoesvalue.id
    );
    requestParams = requestParams.append("referenciaId", referenciaId);

    await firstValueFrom(
      this.httpClient.get<Referencia>(url, { params: requestParams }).pipe(
        tap({
          next: (result: Referencia) => {
            if (result) {
              this.storageService.upsert<Referencia>(
                result,
                this.databaseName,
                this.collectionNameReferencia
              );
              referencia = result;
            }
          },
          error: (error) => {
            this.errorHandlerService.handleError(error);
            if (referencialocal) {
              referencia = referencialocal;
            }
          },
        })
      )
    );
    return referencia;
  }

  updateModificadoReferencia() {
    this.modificadoReferencia$.next(this.modificadoReferencia);
  }

  getModificadoReferencia(): Observable<Referencia> {
    return this.modificadoReferencia$.asObservable();
  }

  private async VerificaCacheReferencias(): Promise<boolean> {
    const lastDocumentsListCheck = await this.dataControleRepositorio.buscar(
      EnumTipoDataControle.DataVerificacaoCacheReferencias
    );

    const tempoEmMinutos = AppConfig.cacheConteudo;

    if (lastDocumentsListCheck) {
      const cacheConteudo: number = tempoEmMinutos * 60 * 1000;
      const ultimaModificacao: Date =
        lastDocumentsListCheck.dataHoraModificacao;
      const DataAtual: Date = new Date();

      if (DataAtual.getTime() - ultimaModificacao.getTime() < cacheConteudo) {
        return true;
      }
    }
    return false;
  }

  private async onMessageReceived(msg: OperacaoSignalR): Promise<void> {
    if (msg && msg.tipoObjeto === EnumTipoObjeto.Referencias) {
      await this.atualizar(msg.dados, false);
    }
  }
}
